import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronRight,
  MoreHorizontal,
  Trash2,
  Pencil,
  Copy,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUpdateCalendar, useCopyCalendar } from '@/hooks/useCalendars'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import type { CalendarDetailResponse } from '@/types'

interface CalendarDetailHeaderProps {
  calendar: CalendarDetailResponse
  calendarId: string
  onDelete: () => void
}

export function CalendarDetailHeader({
  calendar,
  calendarId,
  onDelete,
}: CalendarDetailHeaderProps) {
  const updateCalendar = useUpdateCalendar(calendarId)
  const copyCalendar = useCopyCalendar()

  const [editOpen, setEditOpen] = useState(false)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const [descValue, setDescValue] = useState('')

  const handleEditOpen = () => {
    setNameValue(calendar.name)
    setDescValue(calendar.description ?? '')
    setEditOpen(true)
  }

  const handleConfirmCopy = () => {
    copyCalendar.mutate(
      { id: calendarId, name: `${calendar.name} (Copy)` },
      { onSuccess: () => setCopyDialogOpen(false) }
    )
  }

  const handleSave = () => {
    const trimmedName = nameValue.trim()
    if (!trimmedName) return
    updateCalendar.mutate(
      { name: trimmedName, description: descValue.trim() || null },
      { onSuccess: () => setEditOpen(false) }
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link
          to="/admin/calendars"
          className="hover:text-foreground hover:underline cursor-pointer shrink-0"
        >
          Calendars
        </Link>
        <ChevronRight className="h-4 w-4 shrink-0" />
        <span className="text-foreground font-medium truncate">
          {calendar.name}
        </span>
      </nav>

      {/* Header row */}
      <div className="flex flex-col gap-4 header:flex-row header:items-start header:justify-between">
        <div className="flex flex-col gap-2 min-w-0">
          {/* Title + badge */}
          <div className="flex flex-wrap items-start gap-3 min-w-0 header:flex-nowrap header:items-center">
            <h1 className="text-xl font-semibold text-foreground wrap-break-word header:text-2xl header:truncate">
              {calendar.name}
            </h1>
            <Badge
              variant={calendar.is_active ? 'success' : 'neutral'}
              className="shrink-0"
            >
              {calendar.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {/* Description */}
          {calendar.description && (
            <p className="text-sm text-muted-foreground">
              {calendar.description}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            className="gap-2 cursor-pointer"
            onClick={handleEditOpen}
          >
            <Pencil className="h-4 w-4" />
            <span className="hidden md:inline">Edit</span>
          </Button>
          <Button
            variant="outline"
            className="gap-2 cursor-pointer"
            onClick={() => setCopyDialogOpen(true)}
          >
            <Copy className="h-4 w-4" />
            <span className="hidden md:inline">Copy</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 cursor-pointer"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="cursor-pointer text-destructive focus:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Copy Confirmation Dialog */}
      <ConfirmationDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        title="Copy calendar"
        description={`This will create a copy of "${calendar.name}".`}
        confirmText="Copy"
        cancelText="Cancel"
        onConfirm={handleConfirmCopy}
        isLoading={copyCalendar.isPending}
      />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Calendar</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cal-name">Name</Label>
              <Input
                id="cal-name"
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSave()
                }}
                disabled={updateCalendar.isPending}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cal-desc">Description</Label>
              <Textarea
                id="cal-desc"
                value={descValue}
                onChange={e => setDescValue(e.target.value)}
                placeholder="Add a description..."
                className="resize-none"
                rows={3}
                disabled={updateCalendar.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={updateCalendar.isPending}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!nameValue.trim() || updateCalendar.isPending}
              className="cursor-pointer"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
